import { useQuery } from '@tanstack/react-query';
import { OrderHistory } from '../../../model/orderHistory/OrderHistory';
import { useAllIngredients } from '../../ingredient/hooks/useAllIngredients';
import { useAllProductItems } from '../../product/hooks/useAllProductItems';
import { useUser } from '../../../hooks/useUser';
import { newAbortSignal } from '../../../utils/axiosUtils';
import { usersApiClient } from '../../../clients/users-api';

export const orderHistoryQueryKey = 'orderHistory';

export const useOrderHistory = (
  limit: number = 10
): {
  orderHistory: OrderHistory | null;
  isLoading: boolean;
  error: unknown;
} => {
  const { isUserLoggedIn } = useUser();
  const { ingredients, isLoading: isIngredientsLoading } = useAllIngredients(true);
  const { products, isLoading: isProductsLoading } = useAllProductItems();

  const {
    data: history,
    isLoading: isOrderHistoryLoading,
    error,
  } = useQuery({
    queryKey: [orderHistoryQueryKey, limit, isUserLoggedIn, products],
    queryFn: async ({ signal }) => {
      const orders = await usersApiClient.getUserLatestOrdersWithDetails(newAbortSignal(signal));
      return OrderHistory.fromOrderHistoryData(orders, products, ingredients);
    },
    retryOnMount: false,
    retry: false,
    staleTime: Infinity,
    enabled: Boolean(isUserLoggedIn) && products.length > 0 && ingredients.length > 0,
  });

  return {
    orderHistory: history || null,
    isLoading: isOrderHistoryLoading || isIngredientsLoading || isProductsLoading,
    error,
  };
};
